// src/features/chat/services/chatRepo.js

import { auth, db, storage } from '../../../firebase';
import {
  collection,
  doc,
  setDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  onSnapshot as firestoreOnSnapshot,
  getDocs,
  startAfter,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { compressImage } from '../../../utils/imageCompression';
import { isGroupTarget } from '../models/chatTarget';

/**
 * Get the messages collection for a given target.
 * @param {import('../models/chatTarget').ChatTarget} target
 * @returns {import('firebase/firestore').CollectionReference}
 */
export function getMessagesCollection(target) {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  if (isGroupTarget(target)) {
    return collection(db, `groups/${target.groupId}/messages`);
  }
  return collection(db, `users/${user.uid}/messages`);
}

/**
 * Build the base query for messages.
 * @param {import('../models/chatTarget').ChatTarget} target
 * @param {number} pageSize
 * @returns {import('firebase/firestore').Query}
 */
export function buildMessagesQuery(target, pageSize) {
  const col = getMessagesCollection(target);
  const clauses = [];

  // For groups, only show messages after user joined
  if (isGroupTarget(target)) {
    if (!target.joinedAt) {
      throw new Error('Group chat queries require joinedAt to satisfy security rules');
    }
    clauses.push(where('timestamp', '>=', target.joinedAt));
  }

  clauses.push(orderBy('timestamp', 'desc'), limit(pageSize));
  return query(col, ...clauses);
}

/**
 * Fetch older messages for pagination.
 * @param {import('../models/chatTarget').ChatTarget} target
 * @param {Object} options
 * @param {import('firebase/firestore').DocumentSnapshot} options.cursorDoc - Last doc from previous page
 * @param {number} options.pageSize
 * @returns {Promise<{ docs: Array, nextCursor: DocumentSnapshot|null, hasMore: boolean }>}
 */
export async function fetchOlderMessages(target, { cursorDoc, pageSize }) {
  const col = getMessagesCollection(target);
  const clauses = [];

  if (isGroupTarget(target)) {
    if (!target.joinedAt) {
      throw new Error('Group chat queries require joinedAt to satisfy security rules');
    }
    clauses.push(where('timestamp', '>=', target.joinedAt));
  }

  clauses.push(
    orderBy('timestamp', 'desc'),
    startAfter(cursorDoc),
    limit(pageSize)
  );

  const q = query(col, ...clauses);
  const snap = await getDocs(q);

  const docs = snap.docs.map((d) => ({
    id: d.id,
    ...d.data({ serverTimestamps: 'estimate' }),
  }));

  const nextCursor = snap.docs[snap.docs.length - 1] || null;
  const hasMore = snap.docs.length === pageSize;

  return { docs, nextCursor, hasMore };
}

/**
 * Upload images for chat.
 * @param {import('../models/chatTarget').ChatTarget} target
 * @param {Array<File>} files
 * @returns {Promise<Array<{ url: string, name: string, mimeType: string }>>}
 */
export async function uploadChatImages(target, files) {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const uploads = Array.from(files).map(async (file) => {
    const compressed = await compressImage(file);
    const fileName = `${Date.now()}_${file.name}`;

    // Different storage paths for private vs group
    const basePath = isGroupTarget(target)
      ? `group_chat_images/${target.groupId}`
      : `chat_images/${user.uid}`;

    const storageRef = ref(storage, `${basePath}/${fileName}`);
    await uploadBytes(storageRef, compressed, {
      customMetadata: {
        uploaderId: user.uid,
        uploadTime: new Date().toISOString(),
      },
    });

    const url = await getDownloadURL(storageRef);
    return { url, name: fileName, mimeType: file.type };
  });

  return Promise.all(uploads);
}

/**
 * Write a group user message to Firestore.
 * @param {import('../models/chatTarget').ChatTarget} target
 * @param {Object} options
 * @param {string} options.text
 * @param {Array} [options.images]
 * @param {string|null} [options.replyTo]
 * @returns {Promise<{ messageId: string, messageCreatedAtIso: string }>}
 */
export async function writeGroupUserMessage(target, { text, images = [], replyTo = null }) {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  if (!isGroupTarget(target)) {
    throw new Error('writeGroupUserMessage requires a group chat target');
  }

  const col = getMessagesCollection(target);
  const messageRef = doc(col); // Generate ID without extra read

  const data = {
    text,
    images: images.length ? images : null,
    timestamp: serverTimestamp(),
    inReplyTo: replyTo || null,
    senderId: user.uid,
    type: 'user',
  };

  await setDoc(messageRef, data);

  return {
    messageId: messageRef.id,
    messageCreatedAtIso: new Date().toISOString(),
  };
}

/**
 * Subscribe to messages with real-time updates.
 * Returns the unsubscribe function.
 * @param {import('../models/chatTarget').ChatTarget} target
 * @param {number} pageSize
 * @param {Function} onUpdate - Callback with { docs, cursorDoc, hasMore }
 * @param {Function} [onError] - Error callback
 * @returns {Function} Unsubscribe function
 */
export function subscribeToMessages(target, pageSize, onUpdate, onError) {
  const q = buildMessagesQuery(target, pageSize);

  return firestoreOnSnapshot(
    q,
    (snap) => {
      const docs = snap.docs.map((d) => ({
        id: d.id,
        ...d.data({ serverTimestamps: 'estimate' }),
      }));

      onUpdate({
        docs,
        cursorDoc: snap.docs[snap.docs.length - 1] || null,
        hasMore: snap.docs.length === pageSize,
      });
    },
    (error) => {
      if (onError) onError(error);
    }
  );
}
